<template>
  <div class="form-group custom-form-group" :class="customWrapperClass">
    <label v-if="displayLabel" :for="id" class="custom-form-group-label">{{ label }}</label>
    <span v-if="disclaimer" class="custom-form-group-disclaimer" v-html="disclaimer" />
    <div class="input-group custom-input-group-input custom-input-group-input-suffixed">
      <FlatPickr
        :id="id"
        :name="name"
        :value="flatPickrValue"
        :required="required"
        :disabled="disabled"
        class="form-control custom-input-group-input-field"
        :placeholder="placeholder || $t('eventDate.select')"
        :config="config"
        autocomplete="off"
        @on-change="onChange"
        @on-close="onClose" />
      <input v-if="submitIso" type="hidden" :name="name" :value="isoValue">
      <label :for="id" class="input-group-prepend custom-input-group-input-label">
        <span class="input-group-text custom-prepend custom-prepend-select custom-input-group-input-label-text">
          <i class="demo-icon icon-event-dates" />
        </span>
      </label>
    </div>
  </div>
</template>

<script>
import FlatPickr from "vue-flatpickr-component";
import UIComponentMixin from "../../Mixins/UIComponentMixin";
import { humanize } from "../../helpers";

export default {
  name: "DateTimePicker",

  components: {
    FlatPickr
  },

  mixins: [
    UIComponentMixin
  ],

  props: {
    value: { type: [String, Date] },
    minDate: { type: [Date, String] },
    dateFormat: { type: String },
    name: String,
    labelText: String,
    required: Boolean,
    placeholder: String,
    disclaimer: String,
    displayLabel: { type: Boolean, default: true },
    customWrapperClass: { type: String },
    dateOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    forceUtc: { type: Boolean, default: false },
    timezone: { type: String },
    submitIso: { type: Boolean, default: true }
  },

  data () {
    return {
      isoValue: null,
      flatPickrValue: null,
      config: {
        wrap: true,
        minDate: this.prepareMinDate(this.minDate),
        dateFormat: (this.dateFormat || this.dateOnly) ? (this.dateFormat || "j F Y") : "j F Y at H:i",
        allowInput: true,
        enableTime: !this.dateOnly,
        time_24hr: true,
        disableMobile: this.workingTimezone()
      }
    };
  },

  computed: {
    label () {
      if (this.$useTranslations) {
        if (this.required) {
          return (this.labelText || this.$t(humanize(this.name, this.$useTranslations))) + "*";
        } else {
          return this.labelText || this.$t(humanize(this.name, this.$useTranslations));
        }
      } else {
        if (this.required) {
          return (this.labelText || humanize(this.name)) + "*";
        } else {
          return (this.labelText || humanize(this.name));
        }
      }
    }
  },

  watch: {
    minDate (val) {
      this.$set(this.config, "minDate", this.prepareMinDate(val));
    }
  },

  created () {
    this.initializeIsoString();
    this.initUsedValue();
  },

  methods: {
    initUsedValue () {
      if (this.value) {
        if (this.forceUtc) {
          this.flatPickrValue = new Date(this.$dayjs.utc(this.value).format("YYYY/MM/DD HH:mm"));
        } else if (this.timezone) {
          this.flatPickrValue = new Date(this.$dayjs(this.value).tz(this.timezone).format("YYYY/MM/DD HH:mm"));
        } else {
          this.flatPickrValue = this.value;
        }
      } else {
        this.flatPickrValue = this.value;
      }
    },

    initializeIsoString () {
      if (this.value) {
        if (this.forceUtc) {
          this.isoValue = this.$dayjs(this.value).utc().toISOString();
        } else {
          this.isoValue = this.$dayjs(this.value).toISOString();
        }
      } else {
        this.isoValue = "";
      }
    },

    prepareMinDate (minDate) {
      if (minDate) {
        if (this.forceUtc) {
          return new Date(this.$dayjs.utc(minDate).format("YYYY/MM/DD HH:mm"));
        } else if (this.timezone) {
          return new Date(this.$dayjs(minDate).tz(this.timezone).format("YYYY/MM/DD HH:mm"));
        } else {
          return minDate;
        }
      } else {
        return minDate;
      }
    },

    onChange (selectedDates, dateStr, _instance) {
      const date = selectedDates[0];

      if (dateStr === "") {
        this.updateValue("");
        this.isoValue = "";
      } else if (date) {
        this.updateISOValue(date);
      }
    },

    updateISOValue (date) {
      let isoString;

      isoString = this.prepareIsoString(date);

      this.isoValue = isoString;
      this.updateValue(isoString);
    },

    prepareIsoString (date) {
      if (this.forceUtc) {
        return this.$dayjs(date).utc(true).toISOString();
      } else if (this.timezone) {
        return this.$dayjs(date).tz(this.timezone, true).toISOString();
      } else {
        return this.$dayjs(date).toISOString();
      }
    },

    onClose (_selectedDates, dateStr, _instance) {
      if (dateStr === "") {
        this.updateValue("");
      }
    },

    updateDate () {
      this.$nextTick(function () {
        this.initializeIsoString();
        this.initUsedValue();
      });
    },

    workingTimezone () {
      if (navigator.userAgent.includes("iPhone OS 14_4")) {
        return Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase().includes("beirut");
      } else {
        return false;
      }
    }
  }
};
</script>
